import { MergeAuditLog } from "./types";

// Matches kudzu-backend's customerAuditLogChangeFieldsNames — same labels
// used by the regular "Fields edited:" block, so merge entries read
// consistently with the rest of the audit log. Customer-merge field_changes
// only ever use the first group; patient-merge field_changes (commit_patient_merge.sql)
// only ever use the second group — no overlap, both live in one map for simplicity.
const FIELD_LABELS: Record<string, string> = {
  email: "Email",
  first_name: "First name",
  last_name: "Last name",
  mobile_phone_number: "Phone number",
  additional_phone_number: "Additional phone number",
  date_birth: "Date of birth",
  address: "Address",
  race: "Race",
  ethnicity: "Ethnicity",
  sex: "Sex",
};

type AddressValue = {
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
};

const isAddressValue = (value: unknown): value is AddressValue =>
  typeof value === "object" && value !== null && "city" in value;

// commit_patient_merge.sql's "address" field stores {city, state,
// postal_code, country} on each side, not a plain string — every other
// field_changes value here is a plain string/null.
const formatFieldValue = (value: unknown): string => {
  if (isAddressValue(value)) {
    const parts = [
      value.city,
      value.state,
      value.postal_code,
      value.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
  }
  return (value as string) || "-";
};

const isFieldChangeEntry = (
  value: unknown,
): value is { from: unknown; to: unknown } =>
  typeof value === "object" && value !== null && "to" in value;

// Account Merge AC1/AC2 (customer) + Patient Audit AC1 (patient) — "List of
// fields that were changed and what they were changed to". merge_audit_logs.
// field_changes is jsonb {field: {from, to}}, only ever populated on the
// 'selected' role's row. Formatted to match getAuditLogChangedFieldString's
// "{Field} changed from {old} to {new}".
const getMergeAuditLogFieldsEdited = (
  auditLog: MergeAuditLog,
): string[] | null => {
  const { field_changes: fieldChanges } = auditLog;

  if (
    !fieldChanges ||
    typeof fieldChanges !== "object" ||
    Array.isArray(fieldChanges)
  ) {
    return null;
  }

  const entries = Object.entries(fieldChanges).filter(([, change]) =>
    isFieldChangeEntry(change),
  ) as [string, { from: unknown; to: unknown }][];

  if (!entries.length) return null;

  return entries.map(([field, change]) => {
    const label = FIELD_LABELS[field] ?? field;
    return `${label} changed from ${formatFieldValue(
      change.from,
    )} to ${formatFieldValue(change.to)}`;
  });
};

export default getMergeAuditLogFieldsEdited;
